<template>
    <div>
        <ConfirmDialog
            :is-open="confirmRemoveProductDialog"
            @on-confirm="removeProduct"
            @on-abort="closeConfirmRemoveProductDialog"
        >
            <template #title>
                Usuwanie produktu
            </template>
            <template #content>
                Czy na pewno chcesz usunąć produkt?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>
        <v-container class="pa-0">
            <v-row>
                <v-col cols="12">
                    <v-autocomplete
                        v-model="campaign.customerId"
                        :items="customers"
                        :filter="customFilter"
                        :loading="customersSearchLoading"
                        :search-input.sync="customersSearch"
                        class="v-text-field--placeholder-dark"
                        label="Klient"
                        required
                        :disabled="action === 'updateOutdoorsCampaign'"
                        dense
                        outlined
                        hide-details
                        clearable
                        item-value="id"
                        no-data-text="Nie znaleziono"
                        @change="temporarySaveCampaignData"
                    >
                        <template slot="no-data">
                            <v-list-item>
                                <v-list-item-title>
                                    <template v-if="!customersSearch">
                                        Wpisz treść aby odnaleźć <strong>klienta</strong>.
                                    </template>
                                    <template v-else-if="customersSearchLoading">
                                        Trwa wyszukiwanie...
                                    </template>
                                    <template v-else>
                                        Nie znaleziono
                                    </template>
                                </v-list-item-title>
                            </v-list-item>
                        </template>
                        <template slot="selection" slot-scope="data">
                            {{ data.item.name }}
                        </template>

                        <template slot="item" slot-scope="data">
                            {{ data.item.name }}
                        </template>
                    </v-autocomplete>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="4">
                    <v-text-field
                        v-model="campaign.name"
                        label="Nazwa"
                        outlined
                        dense
                        :error-messages="nameErrors"
                        required
                        @change="temporarySaveCampaignData"
                    />
                </v-col>
                <v-col cols="12" md="4">
                    <v-datetime-picker
                        v-model="startDateTime"
                        label="Początek"
                        date-format="dd.MM.yyyy"
                        :date-picker-props="startDateTimeProps.dateProps"
                        :text-field-props="startDateTimeProps.textFieldProps"
                        :time-picker-props="startDateTimeProps.timeProps"
                        @input="startDateTimeHandler"
                    >
                        <template slot="dateIcon">
                            <v-icon>mdi-calendar</v-icon>
                        </template>
                        <template slot="timeIcon">
                            <v-icon>mdi-clock-outline</v-icon>
                        </template>
                        <template slot="actions" slot-scope="{parent}">
                            <v-btn text color="error" @click.native="parent.clearHandler">
                                Wyczyść
                            </v-btn>
                            <v-btn text color="success" @click="parent.okHandler">
                                Zapisz
                            </v-btn>
                        </template>
                    </v-datetime-picker>
                </v-col>
                <v-col cols="12" md="4">
                    <v-datetime-picker
                        ref="endPicker"
                        v-model="endDateTime"
                        label="Koniec"
                        date-format="dd.MM.yyyy"
                        :date-picker-props="endDateTimeProps.dateProps"
                        :text-field-props="endDateTimeProps.textFieldProps"
                        :time-picker-props="endDateTimeProps.timeProps"
                        :disabled="!isStartDateTimeSelected"
                        @input="endDateTimeHandler"
                    >
                        <template slot="dateIcon">
                            <v-icon>mdi-calendar</v-icon>
                        </template>
                        <template slot="timeIcon">
                            <v-icon>mdi-clock-outline</v-icon>
                        </template>
                        <template slot="actions" slot-scope="{parent}">
                            <v-btn text color="error" @click.native="parent.clearHandler">
                                Wyczyść
                            </v-btn>
                            <v-btn text color="success" @click="parent.okHandler">
                                Zapisz
                            </v-btn>
                        </template>
                    </v-datetime-picker>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" class="mb-0 pb-0">
                    <v-textarea
                        v-model="campaign.description"
                        label="Opis"
                        auto-grow
                        rows="1"
                        outlined
                        dense
                        :error-messages="descriptionErrors"
                        @change="temporarySaveCampaignData"
                    />
                </v-col>
            </v-row>
            <v-row v-if="action === 'createOutdoorsCampaign'" align="center">
                <v-col cols="auto">
                    <h2 class="text-h6">
                        Produkty
                    </h2>
                </v-col>
                <v-col cols="auto">
                    <v-tooltip :disabled="isCustomerSelected" :open-delay="tooltipDelay" top>
                        <template #activator="{on}">
                            <div v-on="on">
                                <v-btn
                                    color="secondary"
                                    fab
                                    small
                                    class="elevation-1"
                                    data-sentry-label="Dodaj produkt"
                                    :disabled="actionLoading || !isCustomerSelected"
                                    @click="addProduct"
                                >
                                    <v-icon>
                                        mdi-plus
                                    </v-icon>
                                </v-btn>
                            </div>
                        </template>
                        <span>Aby dodać produkt wybierz klienta</span>
                    </v-tooltip>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <v-data-table
                        v-if="campaign.campaignProducts.length > 0"
                        :headers="headers"
                        :items="campaign.campaignProducts"
                        disable-pagination
                        hide-default-footer
                        :mobile-breakpoint="0"
                        item-key="index"
                    >
                        <template #item.productName="{item}">
                            {{ item.name }}
                        </template>
                        <template #item.productionFile="{item}">
                            <template v-if="item.campaignProductAttachments">
                                {{ item.campaignProductAttachments.length > 0 ? parseFileName(item.campaignProductAttachments[0].file) : '-' }}
                            </template>
                            <template v-else>
                                <div class="d-inline-flex">
                                    <v-icon
                                        v-if="item.productionFiles.length > 0"
                                        small
                                        color="success"
                                    >
                                        mdi-check
                                    </v-icon>
                                    <v-icon v-else small color="error">
                                        mdi-close
                                    </v-icon>
                                </div>
                            </template>
                        </template>
                        <template #item.amount="{item}">
                            {{ item.amount }}
                        </template>
                        <template #item.weight="{item}">
                            {{ formatWeight(item.weight) }}
                        </template>
                        <template #item.numberOfVersions="{item}">
                            {{ item.numberOfVersions }}
                        </template>
                        <template v-if="action === 'createOutdoorsCampaign'" #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Konfiguracja produktu"
                                            v-on="on"
                                            @click="editProduct(item.index)"
                                        >
                                            <v-icon color="primary">
                                                mdi-cog
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Konfiguracja produktu</span>
                                </v-tooltip>
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Usuń"
                                            v-on="on"
                                            @click="showConfirmRemoveProductDialog(item.index)"
                                        >
                                            <v-icon color="pink">
                                                mdi-delete
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Usuń</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                    <v-alert
                        v-else
                        type="info"
                        text
                        border="left"
                    >
                        Nie wybrano produktów.
                    </v-alert>
                </v-col>
            </v-row>
            <v-row no-gutters justify="end">
                <v-col cols="auto">
                    <v-btn
                        color="primary"
                        depressed
                        class="mt-3 mb-2"
                        data-sentry-label="Następny krok"
                        :disabled="!campaign.customerId"
                        @click="showLocalizations"
                    >
                        Następny krok
                    </v-btn>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script>
import { required, requiredIf } from 'vuelidate/lib/validators'
import { isEmpty, findIndex } from 'lodash'

import ConfirmDialog from '@/components/common/ConfirmDialog'

import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'

import customFilters from '@/mixins/customFilters'
import filters from '@/mixins/dataTableFilters'
import parse from '@/mixins/parse'
import tooltipDelay from '@/mixins/tooltip'
import weightFormatter from '@/mixins/weightFormatter'

let searchTimer

export default {
    name: 'OutdoorsCampaignData',
    components: {
        ConfirmDialog
    },
    mixins: [
        customFilters,
        filters,
        parse,
        tooltipDelay,
        weightFormatter
    ],
    props: {
        action: {
            type: String,
            default: null
        },
        campaignData: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            campaign: {
                name: null,
                customerId: null,
                description: null,
                startDateTime: null,
                endDateTime: null,
                campaignProducts: []
            },
            startDateTimeProps : {
                textFieldProps: {
                    outlined: true,
                    dense: true,
                    appendIcon: 'mdi-calendar',
                },
                dateProps: {
                    firstDayOfWeek: 1,
                    min: new Date().toISOString()
                },
                timeProps: {
                    format: '24hr',
                    scrollable: true
                }
            },
            endDateTimeProps : {
                textFieldProps: {
                    outlined: true,
                    dense: true,
                    appendIcon: 'mdi-calendar',
                    errorMessages: []
                },
                dateProps: {
                    firstDayOfWeek: 1
                },
                timeProps: {
                    format: '24hr',
                    scrollable: true
                }
            },
            startDateTime: null,
            endDateTime: null,
            customersSearch: null,
            customersSearchLoading: false,
            customers: [],
            confirmRemoveProductDialog: false,
            actionLoading: false,
            productIndex: null,
            headers: [
                {
                    text: 'Nazwa produktu',
                    value: 'productName',
                    align: 'left',
                    sortable: false,
                },
                {
                    text: 'Pliki produkcyjne',
                    value: 'productionFile',
                    align: 'center',
                    sortable: false,
                    width: 150
                },
                {
                    text: 'Nakład',
                    value: 'amount',
                    align: 'center',
                    sortable: false,
                    width: 150
                },
                {
                    text: 'Waga',
                    value: 'weight',
                    align: 'center',
                    sortable: false,
                    width: 100
                },
                {
                    text: 'Ilość wzorów',
                    value: 'numberOfVersions',
                    align: 'center',
                    sortable: false,
                    width: 150
                }
            ]
        }
    },
    validations() {
        return {
            campaign: {
                name: { required },
                description: { required },
            },
            endDateTime: {
                required: requiredIf(function() {
                    return this.startDateTime !== null
                })
            }
        }
    },
    computed: {
        savedFilters() {
            return this.$store.state.outdoors.outdoorsCampaigns.outdoorsCampaigns.filters
        },
        filterQuery() {
            const filters = Object.assign({}, this.filters)

            filters.campaign_id = this.campaign.id
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        },
        isCustomerSelected() {
            return typeof this.campaign.customerId !== 'undefined' && this.campaign.customerId !== null
        },
        isStartDateTimeSelected() {
            return typeof this.startDateTime !== 'undefined' && this.startDateTime !== null
        },
        nameErrors() {
            const errors = []

            if (!this.$v.campaign.name.$dirty) return errors
            !this.$v.campaign.name.required && errors.push('Proszę podać nazwę.')

            return errors
        },
        descriptionErrors() {
            const errors = []

            if (!this.$v.campaign.description.$dirty) return errors
            !this.$v.campaign.description.required && errors.push('Proszę podać opis.')

            return errors
        },
    },
    watch: {
        campaignData() {
            if (this.action === 'updateOutdoorsCampaign') {
                this.campaign = JSON.parse(JSON.stringify(this.campaignData))
                this.customers.push(this.campaign.customer)
                this.startDateTime = this.campaign.startDateTime ? new Date(this.campaign.startDateTime) : null
                this.endDateTime = this.campaign.endDateTime ? new Date(this.campaign.endDateTime) : null
            }
        },
        customersSearch(value = null) {
            if (value === null) return

            this.customersSearchLoading = true

            clearTimeout(searchTimer)

            searchTimer = setTimeout(() => {
                if (value) {
                    this.searchCustomers(value)
                } else {
                    this.customersSearchLoading = false
                    this.customers = []
                }
            }, 500)
        },
    },
    created() {
        if (isEmpty(this.$store.state.outdoors.outdoorsCampaigns.outdoorsCampaigns.campaign)) {
            this.temporarySaveCampaignData()
        } else {
            const storedCampaign = this.$store.state.outdoors.outdoorsCampaigns.outdoorsCampaigns.campaign

            this.customers = this.$store.state.outdoors.outdoorsCampaigns.outdoorsCampaigns.customers
            this.campaign = storedCampaign
            this.startDateTime = this.campaign.startDateTime ? new Date(this.campaign.startDateTime) : null
            this.endDateTime = this.campaign.endDateTime ? new Date(this.campaign.endDateTime) : null
            this.indexedCampaignProducts()
        }
        if (!this.$store.state.outdoors.outdoorsCampaigns.outdoorsCampaigns.isCampaignCreated) {
            this.headers.push({
                text: 'Akcje',
                value: 'actions',
                align: 'center',
                sortable: false,
                width: 100
            })
        }
    },
    methods: {
        startDateTimeHandler(date) {
            this.startDateTime = date
            if (date) {
                this.campaign.startDateTime = date.toISOString()
                this.setEndDate(date)
            } else {
                this.validateEndDate()
                this.$refs.endPicker.clearHandler()
                this.campaign.startDateTime = null
            }

            this.temporarySaveCampaignData()
        },
        endDateTimeHandler(date) {
            this.validateEndDate()
            this.endDateTime = date
            date ? this.campaign.endDateTime = date.toISOString() : this.campaign.endDateTime = null
            this.temporarySaveCampaignData()
        },
        setEndDate(date) {
            const selectedStartDate = new Date(date)
            const nextDay = selectedStartDate.setDate(date.getDate() + 1)

            this.endDateTimeProps.dateProps.min = new Date(nextDay).toISOString()
        },
        validateEndDate() {
            this.endDateTimeProps.textFieldProps.errorMessages = []

            !this.$v.endDateTime.required && this.endDateTimeProps.textFieldProps.errorMessages.push('Proszę podać datę zakończenia.')
        },
        indexedCampaignProducts() {
            this.campaign.campaignProducts.forEach(item => {
                delete item.index
            })
            this.campaign.campaignProducts = this.campaign.campaignProducts.map((item, index) => ({
                index,
                ...item
            }))
        },
        addProduct() {
            this.$emit('add-product')
        },
        editProduct(index) {
            this.$emit('edit-product', index)
        },
        temporarySaveCampaignData() {
            const payload = JSON.parse(JSON.stringify({ campaign: this.campaign, customers: this.customers }))

            this.$store.dispatch('outdoors/outdoorsCampaigns/temporarySaveCampaignData', { campaign: payload.campaign, customers: payload.customers })
        },
        showConfirmRemoveProductDialog(index) {
            this.productIndex = index
            this.confirmRemoveProductDialog = true
        },
        removeProduct() {
            this.$store.dispatch('outdoors/outdoorsCampaigns/removeCampaignCreateProductTemplate', this.productIndex)
            this.confirmRemoveProductDialog = false
            this.indexedCampaignProducts()
        },
        closeConfirmRemoveProductDialog() {
            this.productIndex = null
            this.confirmRemoveProductDialog = false
        },
        searchCustomers(query = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie klienta')

            this.$store.dispatch('customers/getCustomers', {
                paginate: '?limit=20',
                query: `&hasOutdoorLocalizations=true&name=${query}`
            })
                .then(response => {
                    this.customers = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.customersSearchLoading = false
                })
        },
        getOutdoorsCampaignLocalizations() {
            this.$store.dispatch('outdoors/outdoorsCampaigns/getOutdoorsCampaignLocalizations', {
                paginate: '?limit=10',
                query:  this.filterQuery
            })
                .then(() => {
                    this.$emit('show-localizations', this.campaign)
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.$emit('localizations-loading', false)
                })
        },
        showLocalizations() {
            this.$v.$touch()
            this.validateEndDate()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }
            this.$emit('localizations-loading', true)
            this.$store.dispatch(`outdoors/outdoorsCampaigns/${this.action}`, this.campaign)
                .then(response => {
                    this.campaign = response.data
                    this.getOutdoorsCampaignLocalizations()
                    this.temporarySaveCampaignData()
                    this.$store.dispatch('outdoors/outdoorsCampaigns/setIsCampaignCreated')
                    const index = findIndex(this.headers, { 'text': 'Akcje'})

                    if (index > -1) this.headers.splice(index, 1)
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.$emit('localizations-loading', false)
                })
        },
    }
}
</script>